import { ChartView } from 'src/Libs/chart-kit'
import { CostSavingChartData, NullablePowerPrice, PowerPrice, ThreeSixtySavings } from '../../types'
import { Moment } from '@e3dc-react/shell/Libs/moment'
import { transformThreeSixtyEnergyAndSpending } from './data'
import { isUndefined } from 'lodash'
import { Resolution } from 'src/Libs/chart-kit/shared/types'
import { incrementDateByResolution } from 'src/Libs/chart-kit/shared/utils/csv-export-fns'

/**
 * Merges the data from the power prices, battery and wallbox data into a single array of CostSavingChartData.
 *
 * Infrequent data resolution: For the day view consumption price data can come in resolutions of 15, 30, 45, 60 minute steps, while all other data comes in 60 minute steps.
 * If there is no data for non-full hour time slots, the value of the last full hour is used.
 *
 * @param powerPriceData
 * @param batteryData
 * @param wallboxData
 * @param from
 * @param to
 * @param view
 * @returns
 */
export const mergeCostSavingData = (
  powerPriceData: NullablePowerPrice[] | PowerPrice[],
  batteryData: ThreeSixtySavings[] | undefined,
  wallboxData: ThreeSixtySavings[] | undefined,
  from: Moment | undefined,
  to: Moment | undefined,
  view: ChartView,
  resolution?: Resolution
): CostSavingChartData[] => {
  if (!from || !to) return []
  const hasResolution = !isUndefined(resolution)

  const data: CostSavingChartData[] = []
  let currentTime = new Date(from?.valueOf() ?? 0)
  const end = new Date(to?.valueOf() ?? 0)

  while (currentTime < end) {
    const time = new Date(currentTime.getTime())
    const { price } = getConsumptionPrice(time, powerPriceData)

    const accessTime = (data: ThreeSixtySavings): Date => new Date(data.time.valueOf())
    const batteryDataPoint = batteryData
      ? binarySearchByView(batteryData, time, hasResolution ? (resolution as ChartView) : view, item => accessTime(item))
      : undefined
    const wallboxDataPoint = wallboxData
      ? binarySearchByView(wallboxData, time, hasResolution ? (resolution as ChartView) : view, item => accessTime(item))
      : undefined

    const { energy: threeSixtyBatteryEnergy, saving: threeSixtyBatterySavings } = transformThreeSixtyEnergyAndSpending(
      batteryDataPoint?.netOutWh,
      batteryDataPoint?.savings
    )

    const { energy: threeSixtyWallboxEnergy, saving: threeSixtyWallboxSavings } = transformThreeSixtyEnergyAndSpending(
      wallboxDataPoint?.netOutWh,
      wallboxDataPoint?.savings
    )

    data.push({
      time: time.valueOf(),
      threeSixtyBatteryProducedEnergy: threeSixtyBatteryEnergy,
      threeSixtyBatterySavings: threeSixtyBatterySavings,
      threeSixtyWallboxProducedEnergy: threeSixtyWallboxEnergy,
      threeSixtyWallboxSavings: threeSixtyWallboxSavings,
      consumptionPrice: price,
    })
    currentTime = hasResolution ? incrementDateByResolution(currentTime, resolution) : incrementDate(currentTime, view)
  }

  return data
}

/**
 * Returns the consumption price for a given time. If the price is not available, it checks if there is a valid price within the last hour.
 * If so, it returns the last valid price. Otherwise, it returns null.
 * This logic is relevant for the day view, where data is mixed with different resolutions.
 *
 * @param time
 * @param powerPriceData
 * @param lastValidConsumptionPriceDate
 * @param consumptionPriceFiller
 * @returns
 */
const getConsumptionPrice = (time: Date, powerPriceData: NullablePowerPrice[] | PowerPrice[]): { price: number | null } => {
  const priceItem = powerPriceData?.find(item => item.validFrom <= time.toISOString() && item.validTo > time.toISOString())

  return { price: priceItem?.price ?? null }
}

/**
 * Binary search for the closest date in the array. How timestamps are compared depends on the view.
 * The array must be sorted by the date in ascending order (oldest first).
 *
 * @param arr
 * @param targetDate
 * @param view
 * @param accessor
 * @returns
 */
const binarySearchByView = <T>(arr: T[], targetDate: Date, view: ChartView, accessor: (item: T) => Date): T | undefined => {
  if (!arr?.length) return undefined

  const targetValue = getComparableTimestamp(targetDate, view)
  let start = 0
  let end = arr.length - 1

  while (start <= end) {
    const mid = Math.floor((start + end) / 2)
    const midDate = accessor(arr[mid])
    const midValue = getComparableTimestamp(midDate, view)

    if (midValue === targetValue) return arr[mid]
    if (targetValue < midValue) {
      end = mid - 1
    } else {
      start = mid + 1
    }
  }

  return undefined
}

/**
 * Returns a comparable timestamp for the given date and view.
 *
 * @param date
 * @param view
 * @returns
 */
const getComparableTimestamp = (date: Date, view: ChartView): number => {
  if (view === 'year') return date.getFullYear() * 100 + date.getMonth() // e.g. 202310 for October 2023

  if (view === 'month' || view === 'week') return date.getFullYear() * 10000 + date.getMonth() * 100 + date.getDate() // e.g. 20231001 for October 1st, 2023

  return date.getTime() // e.g. 202310011200 for October 1st, 2023 at 12:00
}

/**
 * Depending on the view the date is incremented to the next data point timestamp.
 * @param date
 * @param view
 */
const incrementDate = (date: Date, view: ChartView): Date => {
  const incrementedDate = new Date(date.getTime())
  if (view === 'day') incrementedDate.setMinutes(date.getMinutes() + 15)
  if (view === 'week' || view === 'month') incrementedDate.setDate(date.getDate() + 1)
  if (view === 'year') {
    incrementedDate.setMonth(date.getMonth() + 1)
  }
  return incrementedDate
}
